import { useCallback, useEffect, useRef, useState } from "react";
import {
    readStringArrayFromStorage,
    writeStringArrayToStorage,
} from "./useSpreadsheetColumnVisibility";

/**
 * Merges a persisted order with the current default: keeps the stored relative
 * order for fields that still exist and appends any new fields (absent from
 * storage) in their default position at the end.
 */
const reconcileOrder = (
    stored: readonly string[] | undefined,
    defaultOrder: readonly string[],
): readonly string[] => {
    if (!stored) {
        return [...defaultOrder];
    }
    const knownFields = stored.filter((field) => defaultOrder.includes(field));
    const missingFields = defaultOrder.filter(
        (field) => !knownFields.includes(field),
    );
    return [...knownFields, ...missingFields];
};

/**
 * Tracks the display order of spreadsheet columns, persisted to
 * `localStorage` when a `storageKey` is provided. Companion to
 * `useSpreadsheetColumnVisibility`, powering drag-to-reorder in
 * `SpreadsheetColumnSettings`.
 */
export function useSpreadsheetColumnOrder(
    defaultOrder: readonly string[],
    storageKey?: string,
) {
    const [orderedFields, setOrderedFields] = useState<readonly string[]>(
        () => {
            const stored = storageKey
                ? readStringArrayFromStorage(storageKey)
                : undefined;
            return reconcileOrder(stored, defaultOrder);
        },
    );

    // Moves the dragged field to the target field's position (dnd drop).
    const moveField = useCallback((activeField: string, overField: string) => {
        setOrderedFields((prev) => {
            const fromIndex = prev.indexOf(activeField);
            const toIndex = prev.indexOf(overField);
            if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
                return prev;
            }
            const next = [...prev];
            next.splice(fromIndex, 1);
            next.splice(toIndex, 0, activeField);
            return next;
        });
    }, []);

    const reset = useCallback(() => {
        setOrderedFields([...defaultOrder]);
    }, [defaultOrder]);

    // Persist outside the state updater — updaters must stay pure so React
    // can safely invoke them twice in Strict Mode.
    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (storageKey) {
            writeStringArrayToStorage(storageKey, orderedFields);
        }
    }, [orderedFields, storageKey]);

    const hasCustomOrder =
        orderedFields.length !== defaultOrder.length ||
        orderedFields.some((field, index) => field !== defaultOrder[index]);

    return { orderedFields, moveField, reset, hasCustomOrder };
}
